import React from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { 
  TruckIcon, 
  IdentificationIcon, 
  UsersIcon, 
  CalendarIcon,
  ClipboardDocumentListIcon,
  MapPinIcon,
  ArrowPathIcon,
  WrenchIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

const VehicleDetail = ({ vehicle }) => {
  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Fetch vehicle usage history
  const { data: usageData, isLoading, isError } = useQuery(
    ['vehicleUsage', vehicle.id],
    async () => {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';
      const response = await axios.get(`${apiUrl}/admin/vehicles/${vehicle.id}/usage`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data.data;
    },
    {
      enabled: !!vehicle.id,
      onError: (error) => {
        console.error('Error fetching vehicle usage history:', error);
      }
    }
  );

  // Render status badge
  const renderStatusBadge = (status) => {
    // Handle undefined or null status
    if (status === undefined || status === null) {
      return (
        <span className="px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
          Unknown
        </span>
      );
    }
    
    let bgColor = 'bg-gray-100';
    let textColor = 'text-gray-800';
    
    switch (status.toLowerCase()) {
      case 'available':
        bgColor = 'bg-green-100';
        textColor = 'text-green-800';
        break;
      case 'maintenance':
        bgColor = 'bg-yellow-100';
        textColor = 'text-yellow-800';
        break;
      case 'booked':
        bgColor = 'bg-blue-100';
        textColor = 'text-blue-800';
        break;
      case 'unavailable':
        bgColor = 'bg-red-100';
        textColor = 'text-red-800';
        break;
      default:
        break;
    }
    
    return (
      <span className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${bgColor} ${textColor}`}>
        {status}
      </span>
    );
  };

  // Placeholder usage data
  const placeholderUsage = [
    {
      id: 1,
      tour_name: 'Maasai Mara Safari',
      start_date: '2023-04-15',
      end_date: '2023-04-18',
      driver: 'John Kamau',
      status: 'completed'
    },
    {
      id: 2,
      tour_name: 'Amboseli National Park',
      start_date: '2023-05-10',
      end_date: '2023-05-13',
      driver: 'Peter Omondi',
      status: 'completed'
    },
    {
      id: 3,
      tour_name: 'Lake Nakuru Tour',
      start_date: '2023-06-20',
      end_date: '2023-06-22',
      driver: 'Sarah Wanjiku',
      status: 'upcoming'
    }
  ];

  // Use placeholder data if API data is not available
  const usageHistory = usageData || placeholderUsage;

  return (
    <div className="p-6">
      <div className="flex justify-between items-start mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Vehicle Details</h2>
        <div className="flex space-x-2">
          <a 
            href={`/admin/vehicles/${vehicle.id}/maintenance/add`}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <WrenchIcon className="h-4 w-4 mr-2" />
            Add Maintenance
          </a>
          <a 
            href={`/admin/vehicles/${vehicle.id}/expense/add`}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
            Add Expense
          </a>
          {renderStatusBadge(vehicle.status)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Vehicle Information */}
        <div className="space-y-6">
          {/* Vehicle Image */}
          <div className="bg-gray-100 rounded-lg overflow-hidden">
            {vehicle.image ? (
              <img 
                src={`${process.env.REACT_APP_API_URL?.replace('/api/v1', '')}/${vehicle.image}`}
                alt={vehicle.name} 
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/400x200?text=No+Vehicle+Image';
                }}
              />
            ) : (
              <div className="w-full h-64 flex items-center justify-center bg-gray-200">
                <span className="text-gray-500">No image available</span>
              </div>
            )}
          </div>

          {/* Vehicle Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Vehicle Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <TruckIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Vehicle Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{vehicle.name}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <IdentificationIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Registration Number</dt>
                  <dd className="mt-1 text-sm text-gray-900">{vehicle.registration_number}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <TruckIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Type</dt>
                  <dd className="mt-1 text-sm text-gray-900">{vehicle.type}</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <UsersIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Capacity</dt>
                  <dd className="mt-1 text-sm text-gray-900">{vehicle.capacity} persons</dd>
                </div>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                    <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                  </span>
                </div>
                <div className="ml-4">
                  <dt className="text-sm font-medium text-gray-500">Year</dt>
                  <dd className="mt-1 text-sm text-gray-900">{vehicle.model_year}</dd>
                </div>
              </div>
            </div>
          </div>

          {/* Vehicle Description */}
          {vehicle.description && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-900">{vehicle.description}</p>
              </div>
            </div>
          )}

          {/* Vehicle Features */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Features</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              {Array.isArray(vehicle.features) && vehicle.features.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {vehicle.features.map((feature, index) => (
                    <span 
                      key={index} 
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-light text-primary"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No features listed for this vehicle.</p>
              )}
            </div>
          </div>

          {/* Maintenance Records */}
          {vehicle.maintenance_records && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Maintenance Records</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <ul className="space-y-2">
                  {vehicle.maintenance_records.map((record, index) => (
                    <li key={index} className="text-sm">
                      <span className="font-medium">{formatDate(record.date)}</span>: {record.description}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Vehicle Usage History */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-3">Usage History</h3>
          
          {isLoading ? (
            <div className="animate-pulse space-y-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
          ) : isError ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-red-500">Error loading usage history. Please try again.</p>
            </div>
          ) : usageHistory.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-gray-500">No usage history found for this vehicle.</p>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tour
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dates
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Driver
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usageHistory.map((usage) => (
                      <tr key={usage.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{usage.tour_name}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {formatDate(usage.start_date)} - {formatDate(usage.end_date)}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {usage.driver}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            usage.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : usage.status === 'upcoming' 
                                ? 'bg-blue-100 text-blue-800' 
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                            {usage.status.charAt(0).toUpperCase() + usage.status.slice(1)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Vehicle Stats */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-3">Vehicle Statistics</h3>
            <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <MapPinIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Total Distance</dt>
                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      {vehicle.total_distance ? `${vehicle.total_distance.toLocaleString()} km` : 'N/A'}
                    </dd>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <ClipboardDocumentListIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Total Tours</dt>
                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      {vehicle.total_tours || usageHistory.length}
                    </dd>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <ArrowPathIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Last Maintenance</dt>
                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      {vehicle.last_maintenance ? formatDate(vehicle.last_maintenance) : 'N/A'}
                    </dd>
                  </div>
                </div>
              </div>
              <div className="bg-white p-4 rounded shadow">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <span className="flex items-center justify-center h-10 w-10 rounded-md bg-primary-light text-white">
                      <CalendarIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  </div>
                  <div className="ml-4">
                    <dt className="text-sm font-medium text-gray-500">Next Maintenance</dt>
                    <dd className="mt-1 text-sm font-semibold text-gray-900">
                      {vehicle.next_maintenance ? formatDate(vehicle.next_maintenance) : 'N/A'}
                    </dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleDetail;
